// Consent UI: ConsentBanner (first-visit card + settings dialog, reopened from the footer), ConsentGate (placeholder that stands in
// for a third-party embed until the visitor allows embedded media) and useConsent(). State lives in /shared/consent.js.
(function () {
  const { Box, Typography } = MaterialUI;
  const { useState, useEffect } = React;

  function useConsent() {
    const [, bump] = useState(0);
    useEffect(() => {
      const f = () => bump(n => n + 1);
      window.addEventListener('wb-consent', f);
      return () => window.removeEventListener('wb-consent', f);
    }, []);
    return { has: c => window.Consent.has(c), decided: window.Consent.decided() };
  }

  const CATEGORIES = [
    { key: 'necessary', icon: 'lock', title: 'Necessary', locked: true, text: 'Keeps you signed in after you log in and remembers these choices. Always on.' },
    { key: 'preferences', icon: 'palette', title: 'Preferences', text: 'Remembers your colour theme and which sections you left open on this device.' },
    { key: 'analytics', icon: 'monitoring', title: 'Analytics', text: 'Counts page views anonymously: a daily hashed visitor, the country or city of the visit, no IP address stored, kept 90 days. No cookies.' },
    { key: 'media', icon: 'play_circle', title: 'Embedded media', text: 'Loads YouTube videos, Figma prototypes and other embeds (CodePen, Spotify, SoundCloud, Google Maps, Loom). Those services receive your IP address and may set their own cookies.' }
  ];

  function ConsentBanner() {
    const { Card, Button, Dialog, Switch, GoogleIcon } = window.Components;
    const C = window.Consent;
    const { decided } = useConsent();
    const [open, setOpen] = useState(false);
    const [draft, setDraft] = useState({ preferences: false, analytics: false, media: false });

    useEffect(() => {
      const f = () => { setDraft({ preferences: C.has('preferences'), analytics: C.has('analytics'), media: C.has('media') }); setOpen(true); };
      window.addEventListener('wb-consent-open', f);
      return () => window.removeEventListener('wb-consent-open', f);
    }, []);

    const all = { preferences: true, analytics: true, media: true };
    const none = { preferences: false, analytics: false, media: false };
    const decide = choice => { C.save(choice); setOpen(false); };

    return (
      <>
        {!decided && !open && (
          <Card variant="elevated" role="dialog" aria-labelledby="consent-title" aria-describedby="consent-text"
            sx={{ position: 'fixed', zIndex: 1300, left: { xs: 12, md: 24 }, right: { xs: 12, md: 'auto' }, bottom: { xs: 'calc(80px + env(safe-area-inset-bottom, 0px) + 8px)', md: 96 }, maxHeight: { xs: 'calc(100dvh - 180px)', md: 'none' }, overflowY: 'auto', maxWidth: { md: 520 }, borderRadius: 'var(--m3-shape-extra-large)', backgroundColor: 'var(--m3-surface-container-high)', border: '1px solid var(--m3-outline-variant)', boxShadow: 'var(--m3-elevation-3, 0 8px 24px rgba(0,0,0,.25))' }}>
            <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: { xs: 1, md: 1.5 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <GoogleIcon name="cookie" size={24} />
                <Typography id="consent-title" variant="titleLarge" component="h2">Your privacy choices</Typography>
              </Box>
              <Typography id="consent-text" variant="body2" sx={{ color: 'text.secondary' }}>
                This site only stores what it needs to work. With your permission it can also remember your theme, count visits anonymously and load embedded videos and prototypes. You can change this at any time from the footer or account menu.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end', pt: 0.5 }}>
                <Button variant="text" onClick={() => { setDraft(none); setOpen(true); }}>Customize</Button>
                <Button variant="outlined" onClick={() => decide(none)}>Reject non-essential</Button>
                <Button variant="filled" onClick={() => decide(all)}>Accept all</Button>
              </Box>
            </Box>
          </Card>
        )}
        <Dialog open={open} onClose={() => setOpen(false)} headline="Privacy settings" maxWidth="sm"
          actions={<>
            <Button variant="text" onClick={() => decide(none)}>Reject non-essential</Button>
            <Button variant="outlined" onClick={() => decide(all)}>Accept all</Button>
            <Button variant="filled" onClick={() => decide(draft)}>Save choices</Button>
          </>}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
            {CATEGORIES.map(c => (
              <Box key={c.key} sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <GoogleIcon name={c.icon} size={24} style={{ marginTop: 2 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="titleMedium" component="h3">{c.title}</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>{c.text}</Typography>
                </Box>
                <Switch checked={c.locked ? true : !!draft[c.key]} disabled={!!c.locked}
                  onChange={e => setDraft(d => ({ ...d, [c.key]: e.target.checked }))} inputProps={{ 'aria-label': c.title }} />
              </Box>
            ))}
          </Box>
        </Dialog>
      </>
    );
  }

  // Shows `children` once embedded media is allowed; until then a placeholder card. fill: cover the parent (position: absolute).
  // service: 'YouTube' | 'Figma'; href: where the visitor can open the content directly instead.
  function ConsentGate({ service, href, fill = false, minHeight, children }) {
    const { Button, GoogleIcon } = window.Components;
    const { has } = useConsent();
    if (has('media')) return children;
    const C = window.Consent;
    return (
      <Box sx={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, textAlign: 'center', p: 3,
        ...(fill ? { position: 'absolute', inset: 0 } : { minHeight: minHeight || 240, borderRadius: 'var(--m3-shape-large)', border: '1px solid var(--m3-outline-variant)' }),
        backgroundColor: 'var(--m3-surface-container-highest)', color: 'var(--m3-on-surface-variant)'
      }}>
        <GoogleIcon name={service === 'Figma' ? 'draw' : 'play_circle'} size={36} />
        <Typography variant="body2" sx={{ maxWidth: 360 }}>{service} content is blocked until you allow embedded media. {service} receives your IP address and may set cookies.</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'center' }}>
          <Button size="small" variant="filled" onClick={() => C.save({ ...(C.get() || {}), media: true })}>Allow embedded media</Button>
          {href && <Button size="small" variant="text" href={href} target="_blank" rel="noopener noreferrer" icon={<GoogleIcon name="open_in_new" size={16} />}>Open on {service}</Button>}
        </Box>
      </Box>
    );
  }

  window.Components = window.Components || {};
  window.Components.ConsentBanner = ConsentBanner;
  window.Components.ConsentGate = ConsentGate;
  window.useConsent = useConsent;
})();
